'use client'

import { useState } from 'react'
import GrandTitle from '@/components/GrandTitle'
import NewsletterModal from '@/components/NewsletterModal'
import SubPageNav from '@/components/SubPageNav'
import { ou, contenuDe } from '@/lib/contenuEditable'
import type { SectionConfig } from '@/types/site'

/**
 * Page « Club Infini des Visionnaires » — mise en page PREMIUM dédiée.
 *
 * N'emprunte plus la mise en page « Le Cercle » (le mot « cercle » est banni
 * côté cliente) : hero encre marine + constellation, chapitres éditoriaux,
 * les trois piliers en cartes, signature « Demander une invitation ».
 * Textes pilotés par le CRM ; défauts codés en repli.
 */

const INK = '#021734'
const CREAM = '#F7EFE1'
const IVORY = '#FBF6EC'
const ACCENT = '#C2A66B'

interface Bloc {
  eyebrow?: string
  titleAlt?: string
  tagline?: string
  title?: string
  subtitle?: string
  items?: string[]
  apres?: string
  primaryButton?: { label?: string; href?: string }
  secondaryButton?: { label?: string; href?: string }
}
interface Pilier { id?: string; title?: string; subtitle?: string; points?: string[]; apres?: string }

function paragraphes(texte: string): string[] {
  return texte.split('\n\n').filter((p) => p.trim() !== '')
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center">
      <div style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)', letterSpacing: '0.34em', textTransform: 'uppercase', fontWeight: 600, color: ACCENT }}>
        {children}
      </div>
      <div style={{ width: 44, height: 1, background: ACCENT, margin: '16px auto 0', opacity: 0.85 }} aria-hidden />
    </div>
  )
}

function Constellation() {
  return (
    <svg aria-hidden viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
      <g stroke={ACCENT} strokeWidth={0.6} opacity={0.26} fill="none">
        <line x1="160" y1="110" x2="300" y2="180" /><line x1="300" y1="180" x2="410" y2="110" />
        <line x1="880" y1="150" x2="1000" y2="240" /><line x1="1000" y1="240" x2="1080" y2="150" />
        <line x1="200" y1="560" x2="330" y2="500" /><line x1="860" y1="560" x2="980" y2="510" />
      </g>
      <g fill={ACCENT} opacity={0.5}>
        <circle cx="160" cy="110" r="1.8" /><circle cx="300" cy="180" r="2.3" /><circle cx="410" cy="110" r="1.6" />
        <circle cx="880" cy="150" r="1.6" /><circle cx="1000" cy="240" r="2.3" /><circle cx="1080" cy="150" r="1.6" />
        <circle cx="200" cy="560" r="1.8" /><circle cx="330" cy="500" r="2.1" /><circle cx="860" cy="560" r="1.8" /><circle cx="980" cy="510" r="2.1" />
      </g>
      <g fill="#ffffff" opacity={0.22}>
        <circle cx="600" cy="90" r="1" /><circle cx="90" cy="360" r="1" /><circle cx="1130" cy="400" r="1" /><circle cx="640" cy="620" r="1" />
      </g>
    </svg>
  )
}

/** Chapitre éditorial : eyebrow + accroche serif + paragraphes + liste dorée. */
function Chapitre({ c, sur = 'clair' }: { c?: Bloc; sur?: 'clair' | 'ivoire' }) {
  if (!c) return null
  const items = c.items ?? []
  return (
    <section style={{ backgroundColor: sur === 'ivoire' ? IVORY : CREAM, padding: 'clamp(72px, 8.5vw, 116px) 24px' }}>
      <div className="mx-auto text-center" style={{ maxWidth: 740 }}>
        {c.eyebrow && <Eyebrow>{c.eyebrow}</Eyebrow>}
        {c.title && (
          <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(24px, 3vw, 38px)', lineHeight: 1.3, fontWeight: 400, margin: '28px 0 0' }}>{c.title}</p>
        )}
        {c.subtitle && (
          <div style={{ marginTop: 20 }}>
            {paragraphes(c.subtitle).map((p, i) => (
              <p key={i} style={{ fontSize: 16, lineHeight: 1.85, color: 'rgba(22,36,58,0.78)', maxWidth: 640, margin: i === 0 ? '0 auto' : '14px auto 0' }}>{p}</p>
            ))}
          </div>
        )}
        {items.length > 0 && (
          <ul style={{ listStyle: 'none', display: 'inline-flex', flexWrap: 'wrap', gap: '10px 26px', justifyContent: 'center', marginTop: 24 }}>
            {items.map((t, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14.5, color: 'rgba(22,36,58,0.82)' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT }} aria-hidden />{t}
              </li>
            ))}
          </ul>
        )}
        {c.apres && <p style={{ fontSize: 16, lineHeight: 1.85, color: 'rgba(22,36,58,0.78)', maxWidth: 640, margin: '18px auto 0' }}>{c.apres}</p>}
      </div>
    </section>
  )
}

export default function ClubInfiniContenu({ sections }: { sections: SectionConfig[] }) {
  const [modalOpen, setModalOpen] = useState(false)

  const couverture = contenuDe<Bloc>(sections, 'couverture')
  const intro = contenuDe<Bloc>(sections, 'introduction')
  const manifeste = contenuDe<Bloc>(sections, 'manifeste')
  const piliersC = contenuDe<{ eyebrow?: string; title?: string; items?: Pilier[] }>(sections, 'piliers')
  const paris = contenuDe<Bloc>(sections, 'paris')
  const signature = contenuDe<Bloc>(sections, 'signature')

  const piliers = piliersC?.items && piliersC.items.length > 0 ? piliersC.items : []

  return (
    <>
      <SubPageNav />

      <main style={{ backgroundColor: CREAM, color: INK, fontFamily: 'var(--font-body, inherit)' }}>
        {/* ─── HERO ─────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden flex items-center justify-center text-center"
          style={{ minHeight: 'clamp(280px, 42vh, 460px)', color: '#fff', background: 'radial-gradient(ellipse 70% 66% at 50% 42%, #0a2247, #04173a 60%, #021734 100%)' }}
        >
          <Constellation />
          <div className="relative px-6" style={{ zIndex: 2 }}>
            <Eyebrow>{ou(couverture?.eyebrow, 'Club Infini')}</Eyebrow>
            <GrandTitle as="h1" dark style={{ margin: '22px 0 0', maxWidth: '92vw', textShadow: '0 2px 30px rgba(0,0,0,0.4)' }}>
              {ou(couverture?.titleAlt, 'Club Infini des Visionnaires')}
            </GrandTitle>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 'clamp(19px, 2.4vw, 28px)', color: '#e9e0cd', marginTop: 18 }}>
              {ou(couverture?.tagline, 'Sur invitation, par cooptation.')}
            </p>
          </div>
        </section>

        {/* ─── INTRODUCTION ─────────────────────────────────── */}
        <Chapitre c={intro} sur="clair" />

        {/* ─── MANIFESTE ────────────────────────────────────── */}
        <Chapitre c={manifeste} sur="ivoire" />

        {/* ─── LES TROIS PILIERS (cartes) — seulement si renseignés ─── */}
        {piliers.length > 0 && (
        <section style={{ padding: 'clamp(72px, 8.5vw, 116px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 1080 }}>
            <Eyebrow>{ou(piliersC?.eyebrow, 'Les trois piliers du Club')}</Eyebrow>
            <div className="ci-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 44, textAlign: 'left' }}>
              {piliers.map((p, i) => (
                <div key={p.id ?? i} style={{ backgroundColor: IVORY, border: '1px solid rgba(22,36,58,0.1)', padding: '36px 30px' }}>
                  <h3 style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 27, fontWeight: 500, marginBottom: 14 }}>{p.title}</h3>
                  {p.subtitle && paragraphes(p.subtitle).map((par, k) => (
                    <p key={k} style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(22,36,58,0.72)', marginTop: k === 0 ? 0 : 10 }}>{par}</p>
                  ))}
                  {p.points && p.points.length > 0 && (
                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                      {p.points.map((pt, j) => (
                        <li key={j} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, color: 'rgba(22,36,58,0.82)' }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT, flex: 'none' }} aria-hidden />{pt}
                        </li>
                      ))}
                    </ul>
                  )}
                  {p.apres && <p style={{ fontSize: 13.5, lineHeight: 1.7, color: 'rgba(22,36,58,0.62)', fontStyle: 'italic', marginTop: 16 }}>{p.apres}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        )}

        {/* ─── PARIS ────────────────────────────────────────── */}
        <Chapitre c={paris} sur="ivoire" />

        {/* ─── SIGNATURE ────────────────────────────────────── */}
        <section style={{ backgroundColor: INK, color: '#fff', padding: 'clamp(84px, 10vw, 132px) 24px', textAlign: 'center' }}>
          <div className="mx-auto" style={{ maxWidth: 720 }}>
            <div style={{ width: 44, height: 1, background: ACCENT, margin: '0 auto 26px' }} aria-hidden />
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 400, lineHeight: 1.22, marginBottom: 30 }}>
              {ou(signature?.title, 'La force née de rencontres choisies.')}
            </p>
            <div className="gde-btnrow" style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
              <a href={ou(signature?.primaryButton?.href, 'mailto:galadesetoiles@rstars.fr')} className="gde-btn gde-btn--solid">
                {ou(signature?.primaryButton?.label, 'Demander une invitation')}
              </a>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="gde-btn gde-btn--on-dark"
              >
                {ou(signature?.secondaryButton?.label, 'Rester informé')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <style>{`
        @media (max-width: 900px) { .ci-cards { grid-template-columns: 1fr !important; } }
      `}</style>

      <NewsletterModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
